import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Form, Button, Space, message, Spin } from "antd";
import dayjs from "dayjs";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import BannerForm from "../../components/banners/BannerForm";
import BannerLangImages, {
  buildBannerPayload,
  bannerToFormValues,
} from "../../components/banners/BannerLangImages";
import bannerService from "../../services/bannerService";
import masterService from "../../services/masterService";
import { useNotify } from "../../context/NotificationContext";
import { useCommon } from "../../context/CommonContext";

// Stored UTC millisecond timestamp -> DatePicker dayjs value. Read in UTC and
// rebuilt as a local date, so the picker shows the day that was stored rather
// than the day that timestamp falls on locally. undefined when unset.
const toDatePickerValue = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const ms = Number(value);
  if (!Number.isFinite(ms)) return undefined;
  const stored = new Date(ms);
  if (isNaN(stored.getTime())) return undefined;
  return dayjs(
    new Date(stored.getUTCFullYear(), stored.getUTCMonth(), stored.getUTCDate())
  );
};

const EditBanner = () => {
  const navigate = useNavigate();
  const { banner_id } = useParams();
  const [form] = Form.useForm();
  const { setPageTitle } = useCommon();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
const notify = useNotify(); 
  const [formData, setFormData] = useState({
    m_banner_type: undefined,
    show_type: undefined,
  });

  // What the banner looked like when it loaded, so Reset can put it back. Held
  // in a ref rather than state because nothing renders from it.
  const loadedValuesRef = useRef(null);

  const [videoShows, setVideoShows] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    setPageTitle("Edit Banner");
    const loadAll = async () => {
      try {
        setInitialLoading(true);

        const [
          videoShowsRes,
          tvShowsRes,
          languagesRes,
          bannerRes,
        ] = await Promise.allSettled([
          masterService.getVideoShows({
            isAll: true,
          }),
          masterService.getTvShows({
            isAll: true,
          }),
          masterService.getVideoLanguages({
            isAll: true,
            status: "Y",
          }),
          bannerService.getBanner(banner_id),
        ]);

        // Master endpoints answer { data: { data: [...], pagination } }, a few
        // answer { data: [...] }. Unwrap both: handing a pagination wrapper to a
        // dropdown leaves it silently empty.
        const listOf = (result) => {
          if (result.status !== "fulfilled" || !result.value?.data?.status)
            return [];
          const payload = result.value.data.data;
          if (Array.isArray(payload)) return payload;
          if (Array.isArray(payload?.data)) return payload.data;
          return [];
        };

        const activeLanguages = listOf(languagesRes);
        setVideoShows(listOf(videoShowsRes));
        setTvShows(listOf(tvShowsRes));

        let banner = null;
        if (bannerRes.status === "fulfilled") {
          const api = bannerRes.value?.data;
          const payload = api?.data !== undefined ? api.data : api;
          if (Array.isArray(payload?.data)) banner = payload.data[0];
          else if (Array.isArray(payload)) banner = payload[0];
          else if (payload && typeof payload === "object")
            banner = payload.data || payload;
        }

        if (!banner) {
          // A rejected request is not the same as a missing banner: saying "not
          // found" for a 500 or a timeout sends the admin looking for a row that
          // exists. Report the failure and stay put so a reload can retry.
          if (bannerRes.status === "rejected") {
            notify.error({
              message:
                bannerRes.reason?.data?.message ||
                bannerRes.reason?.message ||
                "Could not load this banner. Please try again.",
            });
            return;
          }
          message.error("Banner not found");
          navigate("/banners");
          return;
        }

        // The dropdown asks for active languages only, so a banner sitting on an
        // inactive one would render as a bare id; label it from the lang_name the
        // banner carries through its ms_languages relation.
        const languageById = new Map();
        activeLanguages.forEach((language) =>
          languageById.set(Number(language.lang_id), {
            ...language,
            option_label: language.lang_name,
          })
        );

        const bannerLangId = Number(banner.lang_id);
        if (
          Number.isInteger(bannerLangId) &&
          bannerLangId > 0 &&
          !languageById.has(bannerLangId)
        ) {
          const langName = String(banner.lang_name ?? "").trim();
          languageById.set(bannerLangId, {
            lang_id: bannerLangId,
            lang_name: banner.lang_name ?? null,
            // No name means the language row is gone; the id is all that is left.
            option_label:
              langName.length > 0
                ? `${langName} (Inactive)`
                : `Language ${bannerLangId}`,
          });
        }

        // Same for the image sets: a set on an inactive language must still show
        // its name, or the editor sees a blank picker on a set that has artwork.
        (Array.isArray(banner.images) ? banner.images : []).forEach((set) => {
          const setLangId = Number(set?.lang_id);
          if (
            !Number.isInteger(setLangId) ||
            setLangId <= 0 ||
            languageById.has(setLangId)
          ) {
            return;
          }
          const setLangName = String(set?.lang_name ?? "").trim();
          languageById.set(setLangId, {
            lang_id: setLangId,
            lang_name: set?.lang_name ?? null,
            option_label:
              setLangName.length > 0
                ? `${setLangName} (Inactive)`
                : `Language ${setLangId}`,
          });
        });

        setLanguages([...languageById.values()]);

        setFormData({
          m_banner_type: banner.m_banner_type,
          show_type: banner.show_type,
        });

        const loadedValues = {
          ...bannerToFormValues(banner),
          banner_name: banner.banner_name,
          banner_title: banner.banner_title ?? "",
          // The API returns a UTC unix millisecond timestamp as a digit string;
          // handing it straight to dayjs shows the previous day behind UTC.
          banner_date: toDatePickerValue(banner.banner_date),
          banner_subtitle: banner.banner_subtitle ?? "",
          m_banner_type: banner.m_banner_type,
          show_type: banner.show_type,
          show_id: banner.show_id,
          lang_id: banner.lang_id,
          banner_is_active: banner.banner_is_active ?? "Y",
          content_type: banner.content_type ?? 1,
          // undefined rather than "" or 0 when unset, so an untagged banner stays
          // untagged when saved without touching these.
          content_variant: banner.content_variant ?? undefined,
          content_id: banner.content_id ?? undefined,
        };
        loadedValuesRef.current = loadedValues;
        form.setFieldsValue(loadedValues);
      } catch (e) {
        console.error("Error loading edit banner data:", e);
        notify.error({
          message:
            e?.response?.data?.message ||
            e?.data?.message ||
            e?.message ||
            "Failed to load banner",
        });
      } finally {
        setInitialLoading(false);
      }
    };

    loadAll();
  }, [banner_id, form, navigate]);

  const handleBannerTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      m_banner_type: value,
      show_type: undefined,
    }));
    // lang_id is reset too: it is the show's language, not the artwork
    // languages, which live on the image sets.
    form.setFieldsValue({
      show_type: undefined,
      show_id: undefined,
      lang_id: undefined,
    });
  };

  const handleShowTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, show_type: value }));
    form.setFieldsValue({ show_id: undefined });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      // The artwork fields are form-only.
      const response = await bannerService.editBanner(
        banner_id,
        buildBannerPayload(values)
      );
      if (response?.data?.status) {
        notify.success({
          message: response?.data?.message || "Banner updated successfully",
        });
        navigate("/banners");
      } else {
        notify.error({
          message: response?.data?.message || "Failed to update banner",
        });
      }
    } catch (error) {
      console.error("Error updating banner:", error);
      notify.error({
        // The api interceptor rejects with the axios response, so the server's
        // message sits on error.data.
        message:
          error?.data?.message ||
          error?.response?.data?.message ||
          "Failed to update banner",
      });
    } finally {
      setLoading(false);
    }
  };

  // The uploads and redirect URL sit below the fold, so a validation failure
  // there looks like the button doing nothing — name the first offending field.
  const handleSubmitFailed = ({ errorFields }) => {
    const first = errorFields?.[0];
    notify.error({
      message: first?.errors?.[0] || "Please fix the highlighted fields",
    });
  };

  // Reset means "back to the banner as loaded", not "empty": resetFields alone
  // falls back to initialValues and blanks fields a later save would persist.
  const handleReset = () => {
    form.resetFields();
    if (loadedValuesRef.current) {
      form.setFieldsValue(loadedValuesRef.current);
      setFormData({
        m_banner_type: loadedValuesRef.current.m_banner_type,
        show_type: loadedValuesRef.current.show_type,
      });
    }
  };

  const handleGoBack = () => navigate(-1);

  return (
    <Card style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9' }}>
      <Spin spinning={initialLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleSubmitFailed}
          scrollToFirstError
          initialValues={{
            content_type: 1,
            // Per-language artwork; the banner's own sets replace this on load.
            images: [],
          }}
        >
          {/* No languages prop: the list belongs to the image sets below, not to
              a banner-level language field. */}
          <BannerForm
            form={formData}
            videoShows={videoShows}
            tvShows={tvShows}
            onBannerTypeChange={handleBannerTypeChange}
            onShowTypeChange={handleShowTypeChange}
          >
            <BannerLangImages
              form={form}
              languages={languages}
              bannerType={formData.m_banner_type}
            />
          </BannerForm>

          <Form.Item style={{ marginTop: "32px" }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                Update Banner
              </Button>

              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                size="large"
              >
                Reset
              </Button>

              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleGoBack}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default EditBanner;
